<script setup>
import { ref, computed, onMounted, watch } from "vue";
import tContractorWord from "@/Components/tCon/tContractorWord.vue";
import ScrollToLinkVue from "@/Components/tCon/ScrollToLink.vue";
import { Icon } from "@iconify/vue";
import { Inertia } from "@inertiajs/inertia";
import { useAuthStore } from "@/stores/auth/authStore.js";
import { useComponentTranslations } from "@/Composables/useComponentTranslations";

const showMore1 = ref(false);
const showMore2 = ref(false);
const showMore3 = ref(false);
const showMore4 = ref(false);
const showMore5 = ref(false);
const showMore6 = ref(false);
const showMore7 = ref(false);

const authStore = useAuthStore();

// Load translations for this component
useComponentTranslations({ componentPath: 'Components/tCon/FeaturesGrid' });

const translations = computed(() => authStore.translationsGetter);

const dynamicTranslations = computed(() => authStore.dynamicTranslations);

// Computed properties to ensure reactivity for less and more
// These will automatically update when translations are loaded
const lessText = computed(() => {
  const trans = translations.value;
  // Direct access - Proxy will track this
  if (trans) {
    const value = trans.less;
    // Return the value if it exists, otherwise return 'Less' as fallback
    return value || 'Less';
  }
  return 'Less';
});

const moreText = computed(() => {
  const trans = translations.value;
  if (trans) {
    const value = trans.more;
    return value || 'More';
  }
  return 'More';
});

// Watch translations to force reactivity when keys are added
watch(translations, () => {
  // Access the computed properties to trigger their re-evaluation
  const _ = lessText.value;
  const __ = moreText.value;
}, { deep: true, immediate: true });

// Force load translations on mount to ensure they're available
onMounted(() => {
  // Ensure translations are loaded immediately
  authStore.loadUsedKeysForRoute('Components/tCon/FeaturesGrid');
  authStore.loadUsedKeysForRoute('Components/tCon/FeaturesGrid.vue');
  
  // Force access to less and more to trigger Proxy tracking immediately
  const trans = translations.value;
  if (trans) {
    // Force access to ensure Proxy tracks these keys
    const _less = trans.less;
    const _more = trans.more;
  }
  
  // Debug: Check translations after a delay
  setTimeout(() => {
    const trans = translations.value;
    console.log('FeaturesGrid Debug:', {
      hasLess: 'less' in trans,
      lessValue: trans?.less,
      lessTextValue: lessText.value,
      hasMore: 'more' in trans,
      moreValue: trans?.more,
      moreTextValue: moreText.value,
      allKeys: Object.keys(trans).filter(k => k.includes('less') || k.includes('more'))
    });
  }, 1000);
});

</script>

<template>
  <section class="bg-white">
    <div class="container px-2 sm:px-6 py-10 mx-auto">
      <h1 class="text-3xl font-semibold text-gray-800 capitalize lg:text-4xl">
        {{ translations && translations.explore_our }} <br />
        <span class="underline underline-offset-8 decoration-blue-rgba">
          {{ translations && translations.game_changing_tools }}
        </span>
      </h1>

      <p class="text-2xl capitalize mt-4 text-gray-600 xl:mt-6">
        {{
          translations && translations.more_business_more_profit_less_hassles
        }}
      </p>

      <div
        class="grid grid-cols-1 gap-8 mt-8 xl:mt-12 xl:gap-16 md:grid-cols-2 2xl:grid-cols-3"
      >
        <!-- #1 FEATURE NEWS FEED  -->
        <div class="" id="newsFeedFeature" style="background-color:#F3F3F3;border-radius: 10px;">
          <div class="flex flex-row justify-start p-4" style="align-items: flex-start; " >
            <div
              class="inline-block"
            >
              <img src="/images/icons/news_view.png" width="40" height="40" />
            </div>

            <div class=" w-11/12">
              <h1
                class="pl-2  font-bold " v-html="dynamicTranslations.news_feed_content"
              >
              </h1>
              <div v-show="showMore1">
                <h1
                class="pl-2  font-bold " v-html="dynamicTranslations.news_feed_more_content"
              >
              </h1>
              </div>
              <button
                v-if="!showMore1"
                @click="showMore1 = !showMore1"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600 pl-3"
              >
                {{ translations && translations.more }}
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
              <a
                href="#newsFeedFeature"
                v-if="showMore1"
                @click.prevent="showMore1 = !showMore1; document.querySelector('#newsFeedFeature')?.scrollIntoView({behavior: 'smooth'})"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ lessText }}
              </a>
            </div>
          </div>
        </div>

        <!-- #2 SUB FINDER  -->
        <div class="" id="subFinderFeature"  style="background-color:#F3F3F3;border-radius: 10px;">
          <div class="flex flex-row justify-start p-4" style="align-items: flex-start; ">
            <div
              class="inline-block "
            >
              <img src="/images/icons/contractor.png" width="40" height="40" />
            </div>

            <div class="w-11/12">
              <h1
                class="pl-2 font-bold " v-html="dynamicTranslations.sub_finder_content"
              >

             </h1>
             <div v-show="showMore2">
              <h1
                class="pl-2 font-bold " v-html="dynamicTranslations.sub_finder_more_content"
              >

             </h1>
              </div>

              <button
                v-if="!showMore2"
                @click="showMore2 = !showMore2"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600 pl-3"
              >
                {{ translations && translations.more }}
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>

              <a
                href="#subFinderFeature"
                v-if="showMore2"
                @click.prevent="showMore2 = !showMore2; document.querySelector('#subFinderFeature')?.scrollIntoView({behavior: 'smooth'})"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ lessText }}
              </a>
               
              
            </div>
          </div>
        </div>

        <!-- #3 FEATURE CUSTOMER CHECK  -->
        <div class="" id="customerCheckFeature" style="background-color:#F3F3F3;border-radius: 10px;">
          <div class="flex flex-row justify-start p-4" style="align-items: flex-start;">
            <div
              class="inline-block "
            >
              <img src="/images/icons/redflag.png" width="40" height="40" />
            </div>

            <div class="w-11/12">
              <h1
                class="pl-2  font-bold "
              v-html="dynamicTranslations.red_flag_content">
             
              </h1>

              <div v-show="showMore3">
                <h1
                class="pl-2  font-bold "
              v-html="dynamicTranslations.red_flag_more_content">
             
              </h1>
              </div>

              <button
                v-if="!showMore3"
                @click="showMore3 = !showMore3"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600 pl-3"
              >
                {{ translations && translations.more }}
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
              <a
                href="#customerCheckFeature"
                v-if="showMore3"
                @click.prevent="showMore3 = !showMore3; document.querySelector('#customerCheckFeature')?.scrollIntoView({behavior: 'smooth'})"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ lessText }}
              </a>
            </div>
          </div>
        </div>

        <!-- #4 FEATURE CONTRACTOR PAGE  -->
        <div class="" id="contractorPageFeature" style="background-color:#F3F3F3;border-radius: 10px;">
          <div class="flex flex-row justify-start p-4" style="align-items: flex-start;">
            <div
              class="inline-block "
            >
              <img
                src="/images/icons/contractorpage.png"
                width="40"
                height="40"
              />
            </div>

            <div class=" w-11/12">
              <h1
                class="pl-2 font-bold "
              v-html="dynamicTranslations.contractor_page_content">

              </h1>
              <div v-show="showMore4">
                <h1
                class="pl-2  font-bold "
              v-html="dynamicTranslations.contractor_page_more_content">

              </h1>
              </div>

              <button
                v-if="!showMore4"
                @click="showMore4 = !showMore4"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600 pl-3"
              >
                {{ translations && translations.more }}
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
              <a
                href="#contractorPageFeature"
                v-if="showMore4"
                @click.prevent="showMore4 = !showMore4; document.querySelector('#contractorPageFeature')?.scrollIntoView({behavior: 'smooth'})"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ lessText }}
              </a>
            </div>
          </div>
        </div>

        <!-- #5 FEATURE MENTORING  -->
        <!-- <div class="" id="mentoringFeature">
          <div class="flex flex-row justify-start items-center">
            <div
              class="inline-block p-3 text-blue-500 bg-blue-100 rounded-full"
            >
              <img src="/images/icons/mentor.png" width="40" height="40" />
            </div>

            <div class="flex w-4/5 flex-col justify-center items-start">
              <h1
                class="pl-2 text-2xl min-[450px]:text-3xl sm:text-3xl font-bold text-gray-700 uppercase"
              >
                Mentoring
              </h1>
            </div>
          </div>

          <div class="flex flex-row justify-start items-start">
            <div class="w-1/5"></div>

            <div class="flex w-4/5 flex-col justify-start">
              <div
                class="pl-4 text-xl text-blue-700 text-center font-semibold uppercase tracking-tight"
              >
                <h2>Don't Fool Yourselves</h2>
                <h2 class="capitalize text-black">
                  ... Even the best companies & business managers can ALWAYS use
                  a little <br /><i>(or alot)</i> of mentoring!
                </h2>
              </div>

              <p
                class="mt-1 text-lg text-center font-bold italic tracking-tighter text-red-600 uppercase"
              >
                Free & Valuable Service
              </p>

              <div v-show="showMore5">
                <p class="-ml-4 pt-3 text-blue-rgba uppercase font-extrabold">
                  tContractor provides this Free service by a renowned Master
                  business strategist
                </p>

                <ul
                  class="ml-2 pt-2 pl-3 text-md font-semibold text-slate-700 capitalize list-disc"
                >
                  <li class="">Daily Motivations & Encouragements.</li>
                  <li>Sales Insights.</li>
                  <li>Sales Techniques.</li>
                  <li>Best Practices & Habits.</li>
                </ul>

                <p
                  class="-ml-4 pt-4 text-xl text-rose-700 text-center uppercase font-extrabold"
                >
                  Better Business = Higher Profits & Peace Of Mind!
                </p>
              </div>

              <button
                v-if="!showMore5"
                @click="showMore5 = !showMore5"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600"
              >
                More
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
              <ScrollToLinkVue
                href="#mentoringFeature"
                v-if="showMore5"
                @click="showMore5 = !showMore5"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                Less
              </ScrollToLinkVue>
            </div>
          </div>
        </div> -->

        <!-- #6 FAQ  -->
        <div class="" id="Faq"  style="background-color:#F3F3F3;border-radius: 10px;">
          <div class="flex flex-row justify-start p-4" style="align-items: flex-start; ">
            <div
              class="inline-block "
            >
              <img
                src="/images/icons/faq.png"
                width="40"
                height="40"
              />
            </div>

            <div class=" w-11/12">
              <h1
                class="pl-2 font-bold "
              v-html="dynamicTranslations.faq_content">
               
              </h1>
              <button
                @click="
                  () => {
                    Inertia.visit('/pricing');
                  }
                "
                class="group flex items-center justify-between rounded-xl border border-green-600 bg-green-600 hover:bg-green-800 px-3 py-3 sm:px-5 sm:py-3 mt-8 transition-colors focus:outline-none focus:ring"
              >
                <span class="text-lg font-bold text-white uppercase transition-colors group-hover:font-extrabold group-active:text-indigo-500">{{ translations && translations.more_information }}</span>
              </button>
            </div>
          </div>
          <div class="flex flex-row justify-start items-start">
            <div class="w-1/5"></div>

            <!-- <div class="flex w-4/5 flex-col justify-start">
              <div
                class="pl-4 text-xl text-blue-700 text-center font-bold capitalize tracking-tight"
              >
                <h2>
                  <tContractorWord></tContractorWord>
                  {{ translations && translations.built_managed_by_contractors
                  }}<br />{{
                    translations && translations.for_contractors_subcontractors
                  }}
                </h2>
              </div>

              <p
                class="mt-1 text-lg text-center font-bold italic tracking-tighter text-red-600 uppercase"
              >
                {{ translations && translations.always_free }}<br />{{
                  translations && translations.never_an_upcharge
                }}
              </p>

              <div v-show="showMore6">
                <h2 class="capitalize text-black">
                  tContractor
                  {{ translations && translations.adding_new_features }}
                </h2>

                <p class="-ml-4 pt-3 text-blue-rgba capitalize font-extrabold">
                  <tContractorWord></tContractorWord>
                  {{ translations && translations.important_tool_in_toolbox }}
                  <span class="uppercase text-rose-700">{{
                    translations && translations.better_construction_business
                  }}</span>
                </p>

                <p
                  class="-ml-4 pt-4 text-xl text-rose-700 text-center uppercase font-extrabold"
                >
                  {{ translations && translations.never_upcharge_new_features }}
                </p>
              </div>

              <button
                v-if="!showMore6"
                @click="showMore6 = !showMore6"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600"
              >
                {{ translations && translations.more }}
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
              <ScrollToLinkVue
                href="#newFeatures"
                v-if="showMore6"
                @click="showMore6 = !showMore6"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ lessText }}
              </ScrollToLinkVue>
            </div> -->
          </div>
        </div>


         <!-- #7 FEATURE NEW FEAtURES  -->
        <div class="" id="newFeatures" style="background-color:#F3F3F3;border-radius: 10px;">
          <div  class="flex flex-row justify-start p-4" style="align-items: flex-start; ">
            <div
              class="inline-block "
            >
              <img
                src="/images/icons/under_construc.png"
                width="40"
                height="40"
              />
            </div>

            <div class="w-11/12">
              <h1
                class="pl-2 font-bold "
              v-html="dynamicTranslations.new_feature_content">
               
              </h1>
              <div v-show="showMore7">
                <h1
                class="pl-2 font-bold "
              v-html="dynamicTranslations.new_feature_more_content">
               
              </h1>
              </div>

              <button
                v-if="!showMore7"
                @click="showMore7 = !showMore7"
                class="inline-flex items-center -mx-1 text-md text-blue-500 capitalize transition-colors duration-300 transform hover:underline hover:text-blue-600 pl-3"
              >
                {{ translations && translations.more }}
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </button>
              <ScrollToLinkVue
                href="#newFeatures"
                v-if="showMore7"
                @click="showMore7 = !showMore7"
                class="inline-flex items-center -mx-1 text-md text-teal-500 capitalize transition-colors duration-300 transform hover:underline hover:text-teal-600"
              >
                <svg
                  class="w-4 h-4 mx-1 rtl:-scale-x-100"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    transform="scale(-1,-1) translate(-24,-20)"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ lessText }}
              </ScrollToLinkVue>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
